import React from 'react';
import { Box, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  Checkbox,
  Divider,
  IconButton,
  TableCell,
  TableRow,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { Inbound_Message_Class, SiteManagementLocationFragment } from '@occc/graphql';

import { useSiteDisconnect } from '@occc-app/dataAccessLayer/site/mutations';
import { onErrorDefault } from '@occc-app/dataAccessLayer/utils';
import { formatDateTimeTZ, parseDate, DateTimeFormatTZ } from '@occc-app/lib/date';
import LocationStatusCircle from './LocationStatusCircle';

import { routeCreator, tabValues } from '../../../utils';
import { AdditionalMenuOption } from '../types';

type LocationListItemProps = {
  location: SiteManagementLocationFragment;
  isItemSelected: boolean;
  handleBulkSelect: (id: string) => void;
  additionalMenuOptions?: AdditionalMenuOption[];
  isUserPermittedToEditLocation?: boolean;
};

const LocationListItem = ({
  location,
  isItemSelected,
  handleBulkSelect,
  additionalMenuOptions,
  isUserPermittedToEditLocation,
}: LocationListItemProps) => {
  const navigate = useNavigate();
  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (location: SiteManagementLocationFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(location.uuid);
    };

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const [disconnectSiteMutation] = useSiteDisconnect({
    refetchQueries: ['siteManagementLocation'],
  });

  type DisconnectParams = {
    siteSystemIdentifier: string;
    messageClass: Inbound_Message_Class;
    connectionType: 'mobile' | 'loyalty';
  };

  const handleDisconnect = async ({
    siteSystemIdentifier,
    messageClass,
    connectionType,
  }: DisconnectParams) => {
    if (!siteSystemIdentifier) {
      snackbar.error('Cannot disconnect: this location has no site system identifier configured.');
      return;
    }
    await disconnectSiteMutation({
      variables: {
        input: {
          siteId: siteSystemIdentifier,
          inboundMessageClass: messageClass,
        },
      },
      onCompleted: () => {
        snackbar.success(
          `This site has been disconnected from ${connectionType}. It should reconnect on its own.`,
        );
      },
      onError: onErrorDefault,
    }).catch(() => {
      // onError already surfaced the snackbar; swallow the rejected promise
      // so fire-and-forget callers don't trigger an unhandled rejection.
    });
  };

  const handleDisconnectMobile = (siteSystemIdentifier: string) =>
    handleDisconnect({
      siteSystemIdentifier,
      messageClass: Inbound_Message_Class.Mobile,
      connectionType: 'mobile',
    });

  const handleDisconnectLoyalty = (siteSystemIdentifier: string) =>
    handleDisconnect({
      siteSystemIdentifier,
      messageClass: Inbound_Message_Class.Loyalty,
      connectionType: 'loyalty',
    });

  const getMenuItemAction =
    (routeName: keyof typeof tabValues) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      const routes = {
        generalDetails: routeCreator.generalDetails(location.uuid),
        siteControllerStatus: routeCreator.siteControllerStatus(location.uuid),
        activityLog: routeCreator.activityLog(location.uuid),
        connectionHistory: routeCreator.connectionHistory(location.uuid),
        testingDashboard: routeCreator.testingDashboard(location.uuid),
        scanData: routeCreator.scanData(location.uuid),
      };

      navigate(routes[routeName]);
      handleClose();
    };

  const siteSystemConfiguration =
    location.siteSystemConfiguration.length > 0 ? location.siteSystemConfiguration[0] : null;

  const locationCity = location && location.address ? location.address.city : '';
  const locationState = location && location.address ? location.address.state : '';
  const locationStreet = location && location.address ? location.address.street1 : '';
  const locationZip = location && location.address ? location.address.zipCode : '';
  const isMobileOnline = siteSystemConfiguration?.details?.isMobileOnline || false;
  const isLoyaltyOnline = siteSystemConfiguration?.details?.isLoyaltyOnline || false;
  const lastMobileConnection = siteSystemConfiguration?.details?.lastMobileConnection ?? null;
  const lastLoyaltyConnection = siteSystemConfiguration?.details?.lastLoyaltyConnection ?? null;

  return (
    <TableRow
      key={location.uuid}
      selected={isItemSelected}
      onClick={(event) => {
        event.stopPropagation();
        navigate(routeCreator.generalDetails(location.uuid));
      }}
    >
      {isUserPermittedToEditLocation && (
        <TableCell padding='checkbox'>
          <Checkbox
            checked={isItemSelected}
            onClick={(event) => {
              event.stopPropagation();
              handleBulkSelect(location.uuid);
            }}
          />
        </TableCell>
      )}
      <TableCell>{siteSystemConfiguration?.identifier}</TableCell>
      <TableCell style={{ textAlign: 'center' }}>
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Box display='flex' alignItems='center' gap={0.5}>
            <LocationStatusCircle status={isMobileOnline} />
            <Typography variant='body2'>Mobile</Typography>
          </Box>
          <Box display='flex' alignItems='center' gap={0.5}>
            <LocationStatusCircle status={isLoyaltyOnline} />
            <Typography variant='body2'>Loyalty</Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        {locationStreet}
        <br />
        {`${locationCity}, ${locationState} ${locationZip}`}
      </TableCell>
      <TableCell>
        {lastMobileConnection
          ? formatDateTimeTZ(
              parseDate(lastMobileConnection),
              location.timezone,
              DateTimeFormatTZ.DateTimeWithShortTimeZone,
            )
          : null}
      </TableCell>
      <TableCell>
        {lastLoyaltyConnection
          ? formatDateTimeTZ(
              parseDate(lastLoyaltyConnection),
              location.timezone,
              DateTimeFormatTZ.DateTimeWithShortTimeZone,
            )
          : null}
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(location)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={location.uuid === actionsForUuid}
          onClose={(event) => {
            (event as any).stopPropagation();
            handleClose();
          }}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={getMenuItemAction('generalDetails')}>View Details</MenuItem>

          {isUserPermittedToEditLocation && (
            <>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleDisconnectLoyalty(siteSystemConfiguration?.identifier ?? '');
                  handleClose();
                }}
              >
                Force Disconnect Loyalty
              </MenuItem>

              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleDisconnectMobile(siteSystemConfiguration?.identifier ?? '');
                  handleClose();
                }}
              >
                Force Disconnect Mobile
              </MenuItem>
            </>
          )}

          {additionalMenuOptions && (
            <div>
              <Divider sx={{ my: 1 }} />
              {additionalMenuOptions.map(({ label, onClick }) => (
                <MenuItem
                  key={label}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick?.(event);
                    handleClose();
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </div>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default LocationListItem;
